import React, { FC, useContext, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { IProject, IProjectsResponse } from "interfaces/project";
import { Autocomplete } from "components/Autocomplete";
import { API_URL_PROJECTS } from "constants/api_urls";
import { useQuery } from "@tanstack/react-query";
import { AxiosContext } from "contexts/AxiosContext";
import { makeQueryString } from "core/makeQueryString";

interface ProjectDropdownProps {
  onChange: (selectedProject: IProject | null) => void;
  projectID: number;
}

const DEFAULT_SORTING = "name";

export const ProjectDropdown: FC<ProjectDropdownProps> = ({
  projectID,
  onChange
}) => {
  const { axios } = useContext(AxiosContext)!;

  const fetchProjectsQueryString = makeQueryString({
    ordering: DEFAULT_SORTING
  });

  const fetchProjects = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      axios
        .get<IProjectsResponse>(
          `${API_URL_PROJECTS}${fetchProjectsQueryString}`
        )
        .then(r => r.data),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const selectedProject = useMemo(() => {
    return fetchProjects.data?.results?.find(
      project => project.id === projectID
    );
  }, [projectID, fetchProjects.data?.results]);

  return (
    <>
      <WidthConstrainedAutocomplete
        label="Project"
        disabled={!fetchProjects.data?.results}
        loading={fetchProjects.isLoading}
        error={fetchProjects.error}
        value={selectedProject ?? null}
        size="large"
        autoHighlight
        autoComplete
        options={fetchProjects.data?.results || []}
        noOptionsText="No results found"
        getOptionLabel={(option: IProject) => option.name}
        isOptionEqualToValue={(option: IProject, value: IProject) =>
          option.name === value.name
        }
        onChange={(_event: React.ChangeEvent, value: IProject) => {
          onChange(value);
        }}
        placeholder="Enter project name"
      />
    </>
  );
};

const WidthConstrainedAutocomplete = styled(Autocomplete)`
  padding-top: 8px;
`;
